<template>
  <AgGridVue
    ref="gridRef"
    :grid-options="gridOptions"
    class="ag-theme-material"
    :modules="modules"
    :get-main-menu-items="getMainMenuItems"
    :suppress-movable-columns="suppressMovableColumns"
    :row-style="rowStyle"
    @grid-ready="onGridReady"
    @column-visible="handleColumnsFitContainer"
    @first-data-rendered="handleFirstDataRendered"
    @row-selected="handleSelected"
    @cell-value-changed="handleCellValueChanged"
    @model-updated="handleModelUpdated"
    @drag-stopped="$emit('сolumnMoved', $event)"
    @sort-changed="onSortChanged"
    @column-pinned="$emit('columnPinned', $event)"
    @row-group-opened="handleRowGroupOpened"
    @tool-panel-visible-changed="handleToolPanelVisibleChanged"
  >
  </AgGridVue>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { AgGridVue } from '@ag-grid-community/vue3';
import { LicenseManager } from '@ag-grid-enterprise/core';
import { ColumnsToolPanelModule } from '@ag-grid-enterprise/column-tool-panel';
import { MasterDetailModule } from '@ag-grid-enterprise/master-detail';
import { ExcelExportModule } from '@ag-grid-enterprise/excel-export';
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import { MenuModule } from '@ag-grid-enterprise/menu';
import {
  type GetDetailRowDataParams,
  type SelectionChangedEvent,
  type GridReadyEvent,
  type GridApi,
  type ColDef,
  type GridOptions,
  type RowClassRules,
  type IsRowSelectable,
  type RowHeightParams,
  type ExcelExportParams,
  type ColumnVisibleEvent,
  type RowGroupOpenedEvent,
  type ToolPanelVisibleChangedEvent,
  ModuleRegistry,
} from '@ag-grid-community/core';

import '@ag-grid-community/styles/ag-grid.css';
import '@ag-grid-community/styles/ag-theme-material.css';
import { UiTableColumnSettingsHeader, UiTableLoadingOverlay } from '~/ui';
import { useRoute, useI18n } from '~/infrastructure';
import { splitSortString } from '~/utils';
import type { LocationQueryValue } from 'vue-router';

export type { SelectionChangedEvent };

interface Props {
  headers: ColDef[];
  nestingHeaders?: ColDef[] | undefined;
  items?: any[] | null;
  rowSelection?: 'single' | 'multiple';
  sidebar?: boolean;
  sidebarPosition?: 'right' | 'left';
  isCheckboxSelection: boolean;
  checkboxSelectionPosition?: 'right' | 'left';
  checkboxSelectionPinned?: boolean;
  checkboxSuppressMovable?: boolean;
  masterDetail?: boolean;
  templateDetailCellParams?: any | undefined;
  suppressMovableColumns?: boolean;
  rowStyle?: Record<string, string | number>;
  noRowsTextIdentifier?: string;
  isAllRowsExpanded?: boolean;
  callRecordsField?: string;
  selected?: any[];
  isRowSelectable?: IsRowSelectable;
  rowClassRules: RowClassRules;
  rowHeight: number;
  rowAutoHeight: boolean;
  autoHeaderHeight?: boolean;
  headerDependency?: any;
  getRowHeight: (params: RowHeightParams) => number | undefined | null;
  loading?: boolean;
  suppressDragLeaveHidesColumns?: boolean;
  enableRefreshCells?: boolean;
  headerHeight?: number;
  defaultSort?: LocationQueryValue[] | string | null;
  exportExelTrigger?: boolean;
  defaultExcelExportParams?: ExcelExportParams;
  selectAllOnFirstRender?: boolean;
  suppressContextMenu?: boolean;
  selectionIcon: 'radio' | 'checkbox';
  stopEditingWhenCellsLoseFocus?: boolean;
  getRowId?: (params: any) => string;
}

const props = withDefaults(defineProps<Props>(), {
  getRowId: undefined,
  isCheckboxSelection: false,
  checkboxSelectionPosition: 'left',
  checkboxSelectionPinned: false,
  checkboxSuppressMovable: false,
  masterDetail: false,
  nestingHeaders: undefined,
  rowSelection: undefined,
  templateDetailCellParams: undefined,
  rowStyle: undefined,
  suppressMovableColumns: false,
  noRowsTextIdentifier: 'common.dataTable.noRowsToShow.text',
  isAllRowsExpanded: false,
  items: null,
  callRecordsField: 'callRecords',
  isRowSelectable: () => true,
  rowClassRules: undefined,
  headerDependency: undefined,
  rowHeight: 40,
  selected: () => [],
  rowAutoHeight: true,
  getRowHeight: () => undefined,
  suppressDragLeaveHidesColumns: true,
  enableRefreshCells: false,
  autoHeaderHeight: true,
  headerHeight: 40,
  defaultSort: undefined,
  sidebarPosition: 'right',
  defaultExcelExportParams: undefined,
  selectAllOnFirstRender: false,
  suppressContextMenu: true,
  checkboxColOptions: [],
  selectionIcon: 'checkbox',
  stopEditingWhenCellsLoseFocus: true,
});

const emit = defineEmits<{
  (e: 'update:selected', value: any): void;
  (e: 'gridReady', value: GridApi): void;
  (
    e: 'cellValueChanged',
    value: { column: string; newValue: string; item: any },
  ): void;
  (e: 'сolumnMoved', value: any): void;
  (e: 'sortChanged', value?: string): void;
  (e: 'columnVisible', value: ColumnVisibleEvent): void;
  (e: 'columnPinned', value: any): void;
  (e: 'update:exportExelTrigger', value: boolean): void;
  (e: 'firstDataRendered'): void;
}>();

LicenseManager.setLicenseKey(
  'Using_this_{AG_Grid}_Enterprise_key_{AG-054273}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{ASBISC_Enterprises_PLC}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{Company_internal_software}_only_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_working_on_{Company_internal_software}_need_to_be_licensed___{Company_internal_software}_has_not_been_granted_a_Deployment_License_Add-on___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{29_January_2025}____[v3]_[01]_MTczODEwODgwMDAwMA==242afe413362e8194740bec32ec7ba71',
);

ModuleRegistry.registerModules([
  ColumnsToolPanelModule,
  MasterDetailModule,
  ClientSideRowModelModule,
  MenuModule,
  ExcelExportModule,
]);

const modules = [
  ColumnsToolPanelModule,
  MasterDetailModule,
  ClientSideRowModelModule,
  MenuModule,
  ExcelExportModule,
];

const gridApi = ref<GridApi | undefined>();
const route = useRoute();
const { t } = useI18n();
const gridRef = ref();
const toolPanelElement = ref();

const handleColumnsFitContainer = (event: ColumnVisibleEvent): void => {
  columnsFitContainer(gridApi.value);
  if (event != null) emit('columnVisible', event);
};

const columnsFitContainer = (apiInstanse?: GridApi): void => {
  if (apiInstanse === undefined) return;

  apiInstanse?.sizeColumnsToFit();
};

const setLoading = (value: boolean): void => {
  if (value) {
    gridApi.value?.showLoadingOverlay();
  } else {
    if (props.items?.length === 0) gridApi.value?.showNoRowsOverlay();
    else gridApi.value?.hideOverlay();
  }
};

const filterTableBySearchText = (text: string): void => {
  gridApi.value?.setGridOption('quickFilterText', text);
};

const handleCellValueChanged = (event: any): void => {
  const column = event.column.colId;
  const newValue = event.newValue;
  const item = event.data;
  emit('cellValueChanged', { column, newValue, item });
};

const handleSelected = (): void => {
  setTimeout(() => {
    const getSelectedRows: any[] | undefined =
      gridApi?.value?.getSelectedRows();

    emit('update:selected', getSelectedRows);
  }, 0);
};

const observeGridWidth = (): void => {
  const targetNode = document.querySelector('.ag-root');
  let prevWidth = 0;

  const observer = new ResizeObserver((entries) => {
    for (const entry of entries) {
      const width = entry.borderBoxSize?.[0].inlineSize;
      if (typeof width === 'number' && width !== prevWidth) {
        prevWidth = width;
        columnsFitContainer(gridApi.value);

        gridApi.value?.forEachNode((node) => {
          if (node.expanded) {
            const expandedInstance = node?.detailNode?.detailGridInfo?.api as
              | GridApi
              | undefined;

            columnsFitContainer(expandedInstance);
          }
        });
      }
    }
  });

  if (targetNode != null) {
    observer.observe(targetNode);
  }
};

const onGridReady = (params: GridReadyEvent): void => {
  gridApi.value = params.api;
  observeGridWidth();
  emit('gridReady', params.api);
  setLoading(props.loading);
};

const handleToolPanelVisibleChanged = (
  event: ToolPanelVisibleChangedEvent,
): void => {
  if (event.visible) {
    toolPanelElement.value = gridRef.value?.$el.querySelector('.ag-side-bar');
    document.addEventListener('click', handleToolPanelClickOutside);
  } else {
    document.removeEventListener('click', handleToolPanelClickOutside);
  }
};

const handleToolPanelClickOutside = (event: Event): void => {
  if (
    toolPanelElement.value !== undefined &&
    toolPanelElement.value.contains(event.target) === false
  ) {
    gridApi.value?.closeToolPanel();
  }
};

const getMainMenuItems = (): string[] => {
  return ['pinSubMenu'];
};

const handleRowGroupOpened = (params: RowGroupOpenedEvent): void => {
  highlightMasterRows(params);
};

const highlightMasterRows = (params: RowGroupOpenedEvent): void => {
  const currentIndex = params.node.id;
  const currentMasterRowElement = document.querySelector(
    `.ag-center-cols-container > [row-id="${currentIndex}"]`,
  );
  const currentMasterChildRowElement = document.querySelector(
    `.ag-full-width-container > [row-id^="detail_${currentIndex}"]`,
  );

  const masterRows = document.querySelectorAll('.ag-row');
  const masterChildrenRows = document.querySelectorAll(
    '.ag-full-width-container [row-id^="detail"]',
  );

  masterRows?.forEach((parentTable) => {
    parentTable.classList.remove('ag-row-master-last-expanded');
  });

  masterChildrenRows?.forEach((expandedTable) => {
    expandedTable.classList.remove('ag-row-master-child-last-expanded');
  });

  if (params.node.expanded) {
    currentMasterRowElement?.classList.add('ag-row-master-last-expanded');
    currentMasterChildRowElement?.classList.add(
      'ag-row-master-child-last-expanded',
    );
  }
};

const setRowData = (data: any[] | null): void => {
  if (data === null) return;
  gridApi.value?.setRowData(data);
  if (props.isAllRowsExpanded) setExpandAllRows();
  gridApi.value?.autoSizeAllColumns(true);
  columnsFitContainer(gridApi.value);
};

const setColumnsData = (data: ColDef[]): void => {
  gridApi.value?.setColumnDefs(data);
};

watch(
  () => props.items,
  (data) => {
    setTimeout(() => {
      setRowData(data);
    }, 0);
  },
);

watch(
  () => props.headers,
  (data) => {
    setColumnsData(data);
  },
);

watch(
  () => props.headerDependency,
  () => {
    refreshHeaders();
  },
);

const handleSetSelected = (selected: any[]): void => {
  const isBasketPage = route.path.includes('/basket');

  gridApi.value?.forEachNode((node) => {
    const checkByLineId = (item: any): any => item.lineId === node.data.lineId;
    const checkByProductId = (item: any): any => item.id === node.data.id;

    if (
      selected.find(isBasketPage ? checkByLineId : checkByProductId) !==
      undefined
    ) {
      node.setSelected(true);
    } else {
      node.setSelected(false);
    }
  });
};

const handleModelUpdated = (): void => {
  if (props.selected.length !== 0) handleSetSelected(props.selected);
};

const selectAll = (): void => {
  gridApi.value?.selectAll();
};

const fitColumns = (apiInstance?: GridApi): void => {
  // HACK for initial fit container width
  setTimeout(() => {
    apiInstance?.autoSizeAllColumns(true);
    columnsFitContainer(apiInstance);
  }, 0);
};

const handleFirstDataRendered = (): void => {
  handleSetSelected(props.selected);
  if (props.isAllRowsExpanded) setExpandAllRows();

  fitColumns(gridApi.value);

  if (props.defaultSort !== undefined) {
    setDefaultSort(props.defaultSort);
  }

  if (props.selectAllOnFirstRender) {
    selectAll();
  }

  emit('firstDataRendered');
};

watch(
  () => props.defaultSort,
  () => {
    if (props.defaultSort !== undefined) {
      setDefaultSort(props.defaultSort);
    }
  },
);

const setDefaultSort = (defaultSort: string): void => {
  const { field, sort } = splitSortString(defaultSort);

  const columnsState = gridApi.value
    ?.getColumnState()
    ?.map((state) =>
      state.colId === field ? { ...state, sort } : { ...state, sort: null },
    );

  gridApi.value?.applyColumnState({
    state: columnsState,
  });
};

const refreshHeaders = (): void => {
  gridApi.value?.refreshHeader();
};

const exportExel = (): void => {
  gridApi.value?.exportDataAsExcel();
};

watch(
  () => props.selected,
  (newValue) => {
    handleSetSelected(newValue);
  },
);

watch(
  () => props.items,
  () => {
    if (props.enableRefreshCells) gridApi.value?.refreshCells();
  },
  { deep: true },
);

defineExpose({
  filterTableBySearchText,
  gridRef,
});

const setExpandAllRows = (): void => {
  gridApi.value?.forEachNode((node): void => {
    node.setExpanded(true);
  });
};

const checkboxCol = {
  checkboxSelection: true,
  headerCheckboxSelection: true,
  resizable: false,
  maxWidth: 40,
  suppressColumnsToolPanel: true,
  suppressMenu: true,
  pinned: props.checkboxSelectionPinned
    ? props.checkboxSelectionPosition
    : false,
  suppressMovable: props.checkboxSuppressMovable,
};

const actionsColDefault = computed(() => {
  return {
    field: 'actions',
    headerName: '',
    ...(props.sidebar &&
      props.sidebarPosition === 'right' && {
        headerComponent: UiTableColumnSettingsHeader,
      }),
    suppressColumnsToolPanel: true,
    pinned: 'right',
    lockPinned: true,
    menuTabs: [],
  };
});

const headers = computed(() => {
  const actionsHeader = props.headers.find(
    (header) => header.field === 'actions',
  );
  const pinned = actionsHeader?.pinned;

  const actionsCol =
    pinned != null
      ? { ...actionsColDefault.value, pinned }
      : { ...actionsColDefault.value };

  return [
    ...props.headers.filter((header) => header.field !== 'actions'),
    ...(actionsHeader !== undefined
      ? [
          {
            ...actionsHeader,
            ...actionsCol,
          },
        ]
      : []),
  ];
});

const columnDefs = computed<ColDef[]>(() => [
  ...(props.checkboxSelectionPosition === 'left' &&
  props.rowSelection !== undefined &&
  props.isCheckboxSelection
    ? [checkboxCol]
    : []),
  ...headers.value,
  ...(props.checkboxSelectionPosition === 'right' &&
  props.rowSelection !== undefined &&
  props.isCheckboxSelection
    ? [checkboxCol]
    : []),
]);

const defaultColDef: ColDef = {
  autoHeaderHeight: props.autoHeaderHeight,
  autoHeight: props.rowAutoHeight,
  wrapHeaderText: true,
  menuTabs: ['generalMenuTab'],
};

// TODO check this autoSizeStrategy, may be it extra and not need
const fitCellContentsCols = computed(() => {
  return props.headers
    .filter((col) => col.supressFitCellContents !== true)
    .map((col) => col.field);
});

// TODO check this autoSizeStrategy, may be it extra and not need
const fitCellNestedContentsCols = computed(() => {
  return props.nestingHeaders !== undefined
    ? props.nestingHeaders
        ?.filter((col) => col.supressFitCellContents !== true)
        .map((col) => col.field)
    : undefined;
});

const gridOptions = computed<GridOptions>(() => ({
  getRowId: props.getRowId,
  columnDefs: columnDefs.value,
  rowData: props.items,
  defaultColDef,
  sortingOrder: ['desc', 'asc', null],
  isRowSelectable: props.isRowSelectable,
  rowSelection: props.rowSelection,
  headerHeight: props.headerHeight,
  getRowHeight: props.getRowHeight,
  rowHeight: props.rowHeight,
  rowClassRules: props.rowClassRules,
  animateRows: true,
  overlayNoRowsTemplate: `<span>${t(props.noRowsTextIdentifier)}</span>`,
  autoSizeStrategy: {
    type: 'fitCellContents',
    skipHeader: true,
    ...(fitCellContentsCols.value.length > 0 && {
      columnKeys: fitCellContentsCols.value,
    }),
  },
  sideBar: {
    toolPanels: [
      ...(props.sidebar
        ? [
            {
              id: 'columns',
              labelDefault: '',
              labelKey: '',
              iconKey: 'columns',
              toolPanel: 'agColumnsToolPanel',
              width: 225,
              toolPanelParams: {
                suppressColumnFilter: true,
                suppressColumnSelectAll: true,
                suppressPivotMode: true,
                suppressRowGroups: true,
                suppressValues: true,
              },
            },
          ]
        : []),
    ],
    defaultToolPanel: '',
    position: props.sidebarPosition,
  },
  suppressRowClickSelection: true,
  detailRowAutoHeight: true,
  masterDetail: props.masterDetail,
  ...(props.masterDetail && {
    detailCellRendererParams: {
      detailGridOptions: {
        headerHeight: 36,
        suppressRowClickSelection: true,
        enableRangeSelection: true,
        columnDefs: props.nestingHeaders,
        defaultColDef: {
          sortable: false,
          flex: 1,
          autoHeight: true,
        },
        rowHeight: 56,
        autoSizeStrategy: {
          type: 'fitCellContents',
          skipHeader: false,
          ...(fitCellNestedContentsCols.value !== undefined &&
            fitCellNestedContentsCols.value.length > 0 && {
              columnKeys: fitCellNestedContentsCols.value,
            }),
        },
        suppressDragLeaveHidesColumns: props.suppressDragLeaveHidesColumns,
      },
      getDetailRowData: (params: GetDetailRowDataParams) => {
        const currentApi = params.node.detailGridInfo.api as GridApi;
        currentApi.addEventListener('firstDataRendered', () => {
          fitColumns(currentApi);
        });

        params.successCallback(params.data[props.callRecordsField] as any[]);
      },
      template: props.templateDetailCellParams,
    },
  }),
  singleClickEdit: true,
  stopEditingWhenCellsLoseFocus: props.stopEditingWhenCellsLoseFocus,
  domLayout: 'autoHeight',
  suppressPaginationPanel: true,
  loadingOverlayComponent: UiTableLoadingOverlay,
  suppressDragLeaveHidesColumns: props.suppressDragLeaveHidesColumns,
  defaultExcelExportParams: props.defaultExcelExportParams,
  suppressContextMenu: props.suppressContextMenu,
}));

watch(
  () => props.loading,
  (newValue) => {
    setLoading(newValue);
  },
);

watch(
  () => props.exportExelTrigger,
  (value) => {
    if (value) {
      exportExel();

      emit('update:exportExelTrigger', false);
    }
  },
);

const onSortChanged = (value): void => {
  const sortedCol = value.columnApi
    .getColumnState()
    .find((column) => column.sort !== null);

  if (sortedCol !== undefined)
    emit('sortChanged', `${sortedCol?.colId}_${sortedCol?.sort}`);
  else emit('sortChanged', undefined);

  refreshHeaders();
};

const SELECTION_ICON_MAP = {
  checkbox: {
    checked: `url('/svg/checkbox-checked.svg')`,
    unchecked: `url('/svg/checkbox-unchecked.svg')`,
    indeterminate: `url('/svg/checkbox-indeterminate.svg')`,
  },
  radio: {
    checked: `url('/svg/radio-checked.svg')`,
    unchecked: `url('/svg/radio-unchecked.svg')`,
    indeterminate: `url('/svg/radio-unchecked.svg')`,
  },
};

const currentSelectionIcon = computed(() =>
  props.selectionIcon === 'checkbox'
    ? SELECTION_ICON_MAP.checkbox
    : SELECTION_ICON_MAP.radio,
);
</script>

<style scoped lang="scss">
$table-scroll-height: 6px;
.ag-theme-material {
  --ag-foreground-color: rgb(var(--v-theme-black));
  --ag-background-color: rgb(var(--v-theme-white));
  --ag-selected-row-background-color: rgb(var(--v-theme-blue-50));
  --ag-range-selection-border-color: transparent;

  --ag-header-foreground-color: rgb(var(--v-theme-grey-800));
  --ag-header-background-color: rgb(var(--v-theme-white));

  --ag-header-cell-hover-background-color: rgb(var(--v-theme-grey-50));
  --ag-header-cell-moving-background-color: rgb(var(--v-theme-grey-50));

  --ag-odd-row-background-color: rgb(var(--v-theme-white));
  --ag-header-column-resize-handle-color: rgb(var(--v-theme-grey-800));
  --ag-row-border-color: rgb(var(--v-theme-grey-100));

  --ag-header-column-resize-handle-display: block;
  --ag-header-column-resize-handle-height: 25%;
  --ag-header-column-resize-handle-width: 2px;
  --ag-header-column-resize-handle-color: rgb(var(--v-theme-grey-100));

  --ag-font-size: 14px;
  --ag-font-family: Arimo;
  --ag-grid-size: 8px;
  --ag-line-height: 18px;

  --ag-checkbox-checked-color: rgb(var(--v-theme-primary));
  --ag-checkbox-unchecked-color: rgb(var(--v-theme-grey-400));
}

:deep(.ag-cell) {
  display: flex;
  align-items: center;
  padding: 8px 8px;
  line-height: inherit;
}

:deep(.ag-cell--align-start) {
  align-items: flex-start;
}

:deep(.ag-cell-wrapper) {
  width: 100%;
  align-items: center;
}

:deep(.ag-header-cell) {
  padding: 0 8px;
  font-weight: 400;
}

:deep(.ag-checkbox-input-wrapper) {
  width: 24px;
  height: 24px;
}

:deep(.ag-checkbox-input-wrapper::after) {
  content: v-bind('currentSelectionIcon.unchecked');
}

:deep(.ag-checkbox-input-wrapper.ag-checked::after) {
  content: v-bind('currentSelectionIcon.checked');
}

:deep(.ag-checkbox-input-wrapper.ag-indeterminate::after) {
  content: v-bind('currentSelectionIcon.indeterminate');
}

:deep(.ag-icon-tree-closed) {
  font-family: inherit;
}

:deep(.ag-icon-tree-closed) {
  font-family: inherit;
}

:deep(.ag-icon-tree-closed::before) {
  content: url('/svg/plus.svg');
  font-family: inherit;
  height: 20px;
  display: block;
}

:deep(.ag-icon-tree-open::before) {
  content: url('/svg/minus.svg');
  font-family: inherit;
  height: 20px;
  display: block;
}

:deep(.ag-cell-expandable.ag-cell-wrapper .ag-group-contracted) {
  height: auto;
}

:deep(.ag-cell-expandable.ag-cell-wrapper .ag-group-expanded) {
  height: auto;
}

:deep(.ag-details-row) {
  padding: 15px 15px 23px;
}

:deep(
  .ag-checkbox-input-wrapper:focus-within,
  .ag-checkbox-input-wrapper:active
) {
  box-shadow: none;
}

:deep(.basket-table-header) {
  justify-content: center;

  .ag-header-cell-label {
    justify-content: center;
  }
}

:deep(.ag-side-buttons) {
  display: none;
}

:deep(.ag-column-select-header) {
  display: none;
}

:deep(.ag-column-select-virtual-list-item) {
  padding: 0 16px;
}

:deep(.ag-virtual-list-viewport) {
  padding: 12px 0;
}

:deep(.ag-column-select) {
  border-bottom: none;
  overflow: visible;
}

:deep(.ag-column-select-list) {
  overflow: visible;
}

:deep(.ag-column-panel-column-select) {
  border-top: none;
}

:deep(.ag-header-group-cell--blue::before) {
  position: absolute;
  left: 5px;
  height: 100%;
  width: calc(100% - 10px);
  content: '';
  background-color: rgb(var(--v-theme-blue-50));
  z-index: -1;
  text-align: center;
}

:deep(.ag-header-group-cell--center) {
  padding: 0 8px;
  justify-content: center;
}

:deep(.ag-header-group-cell--center .ag-header-group-cell-label) {
  justify-content: center;
}

:deep(.ag-header-group-cell) {
  font-weight: 400;
}

:deep(.ag-cell-inline-editing) {
  border: 1px solid transparent;
  border-radius: var(--ag-card-radius);
  box-shadow: none;
  padding: var(--ag-grid-size);
  height: 100%;
  background-color: transparent;
  &:focus-within {
    border: 1px solid transparent !important;
  }
}

:deep(.ag-body-horizontal-scroll) {
  position: absolute;
  top: 38px;
  height: $table-scroll-height !important;
  max-height: $table-scroll-height !important;
  min-height: $table-scroll-height !important;

  /* width */
  ::-webkit-scrollbar {
    height: $table-scroll-height;
  }

  /* Track */
  ::-webkit-scrollbar-track {
    max-width: calc(100% - 10px);
    border-radius: 10px;
    border-left: 2px solid white;
    border-right: 2px solid white;
    background-color: rgb(var(--v-theme-grey-100));
  }

  /* Handle */
  ::-webkit-scrollbar-thumb {
    border-radius: 10px;
    background-color: rgb(var(--v-theme-grey-200));
  }

  /* Handle on hover */
  ::-webkit-scrollbar-thumb:hover {
    background: rgb(var(--v-theme-blue-500));
  }

  .ag-horizontal-left-spacer {
    overflow-x: hidden;
  }

  .ag-body-horizontal-scroll-viewport {
    height: $table-scroll-height !important;
    max-height: $table-scroll-height !important;
    min-height: $table-scroll-height !important;
  }

  .ag-body-horizontal-scroll-viewport {
    overflow-x: auto;
  }

  .ag-horizontal-right-spacer {
    opacity: 0;
  }
}

:deep(.separator-header-row) {
  background: rgb(var(--v-theme-blue-100));
  left: 0 !important;
  width: 100% !important;
  padding-left: 8px;
}

:deep(.ag-cell-wrap-text) {
  word-break: normal;
}

:deep(.ag-row-disabled) {
  background-color: rgb(var(--v-theme-grey-50));

  & > .ag-cell:not(.ag-cell-cannot-be-disabled) {
    pointer-events: none;
    opacity: 0.4;
  }
}

:deep(.ag-overlay-wrapper) {
  align-items: start;
  padding: 128px 0;
}

:deep(.ag-row-master-last-expanded) {
  background-color: rgb(var(--v-theme-blue-50));
}

:deep(.ag-row-master-child-last-expanded) {
  transition: all 0.2s ease-in-out;

  .ag-row,
  .ag-header-row {
    background-color: rgba(var(--v-theme-blue-50), 0.5);
  }
}

:deep(.ag-details-grid .ag-center-cols-viewport) {
  min-height: auto !important;
}

:deep(.ag-theme-material .ag-layout-auto-height .ag-center-cols-clipper) {
  min-height: inherit;
}

:deep(.ag-side-bar.ag-sidebar--disabled) {
  pointer-events: none;
}

:deep(.ag-side-bar.ag-sidebar--disabled .ag-tool-panel-wrapper) {
  &::before {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(#fafafa, 0.6);
    z-index: 3;

    display: block;
    content: '';
  }
}

:deep(.ag-side-bar) {
  position: absolute;
  top: 50px;
  border-radius: 16px;
  left: 5px;
  overflow-y: auto;
  overflow-x: hidden;
  z-index: 1;
  max-height: calc(100% - 60px);
  box-shadow: 0 0 20px 10px rgba(0, 0, 0, 0.05);

  .ag-tool-panel-wrapper {
    height: 100%;
    background: rgb(var(--v-theme-white));
  }

  .ag-column-select-column-label {
    color: rgb(var(--v-theme-grey-800));
  }
}

:deep(.ag-cell--disabled) {
  opacity: 0.3;
  pointer-events: none;
}

@keyframes highlightFade {
  from {
    background-color: rgb(var(--v-theme-blue-50));
  }
}

:deep(.ag-row--highlight) {
  animation: highlightFade 4s cubic-bezier(0.25, 0.1, 0.25, 1);
}
</style>

<style lang="scss">
div.ag-root {
  .ag-cell-focus:not(.ag-header-cell),
  .v-btn {
    user-select: text;
  }
}
</style>
